import { useCallback, useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { PageFrame } from '../components/work/page-chrome';
import { ErrorAlert, LoadingRows, PageTitle } from '../components/work/page-states';
import { apiErrorMessage, apiFetch, v1Path } from '../lib/api';
import type { ProjectDto } from '../lib/work-types';

const LINK_STATES = ['idle', 'connecting', 'qr', 'connected'] as const;
type LinkState = (typeof LINK_STATES)[number];
const NONE = '__none__';

type WhatsAppStatus = {
  state: LinkState;
  qr_data_url: string | null;
  linked_phone: string | null;
  default_project: string | null;
  error: string | null;
};

function isLinkState(value: unknown): value is LinkState {
  return typeof value === 'string' && (LINK_STATES as readonly string[]).includes(value);
}

function isWhatsAppStatus(value: unknown): value is WhatsAppStatus {
  if (!value || typeof value !== 'object') return false;
  const rec = value as Record<string, unknown>;
  return (
    isLinkState(rec.state)
    && (rec.qr_data_url === null || typeof rec.qr_data_url === 'string')
    && (rec.linked_phone === null || typeof rec.linked_phone === 'string')
    && (rec.default_project === null || typeof rec.default_project === 'string')
    && (rec.error === null || typeof rec.error === 'string')
  );
}

function isProjectDto(value: unknown): value is ProjectDto {
  if (!value || typeof value !== 'object') return false;
  const rec = value as Record<string, unknown>;
  return (
    typeof rec.id === 'string'
    && typeof rec.identifier === 'string'
    && typeof rec.name === 'string'
  );
}

function statusLabel(status: WhatsAppStatus): string {
  if (status.state === 'connected') {
    return status.linked_phone ? `Linked as ${status.linked_phone}` : 'Linked';
  }
  if (status.state === 'qr') return 'Scan the QR with the Pulse bot phone';
  if (status.state === 'connecting') return 'Connecting…';
  return 'Not linked';
}

export default function WhatsApp() {
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [savingProject, setSavingProject] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [nextStatus, projectRes] = await Promise.all([
        apiFetch<unknown>(v1Path('/whatsapp')),
        apiFetch<unknown>(v1Path('/projects?limit=100&offset=0')),
      ]);
      if (!isWhatsAppStatus(nextStatus)) {
        throw new Error('WhatsApp status was malformed.');
      }
      if (!projectRes || typeof projectRes !== 'object' || !Array.isArray((projectRes as { items?: unknown }).items)) {
        throw new Error('Project list was malformed.');
      }
      const items = (projectRes as { items: unknown[] }).items.filter(isProjectDto);
      setStatus(nextStatus);
      setProjects(items);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load WhatsApp settings'));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!status || (status.state !== 'qr' && status.state !== 'connecting')) {
      return;
    }
    const timer = window.setInterval(() => {
      void load();
    }, 2000);
    return () => window.clearInterval(timer);
  }, [load, status]);

  async function onLink() {
    setBusy(true);
    setError(null);
    try {
      const next = await apiFetch<unknown>(v1Path('/whatsapp/link'), { method: 'POST' });
      if (!isWhatsAppStatus(next)) {
        throw new Error('WhatsApp status was malformed.');
      }
      setStatus(next);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not start WhatsApp linking'));
    } finally {
      setBusy(false);
    }
  }

  async function onUnlink() {
    setBusy(true);
    setError(null);
    try {
      const next = await apiFetch<unknown>(v1Path('/whatsapp/unlink'), { method: 'POST' });
      if (!isWhatsAppStatus(next)) {
        throw new Error('WhatsApp status was malformed.');
      }
      setStatus(next);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not disconnect WhatsApp'));
    } finally {
      setBusy(false);
    }
  }

  async function onDefaultProject(value: string) {
    setSavingProject(true);
    setError(null);
    try {
      const next = await apiFetch<unknown>(v1Path('/whatsapp'), {
        method: 'PATCH',
        body: JSON.stringify({ default_project: value === NONE ? null : value }),
      });
      if (!isWhatsAppStatus(next)) {
        throw new Error('WhatsApp status was malformed.');
      }
      setStatus(next);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the default project'));
    } finally {
      setSavingProject(false);
    }
  }

  return (
    <PageFrame>
      <div className="flex flex-1 flex-col gap-6">
        <PageTitle title="WhatsApp" />
        <p className="max-w-2xl text-sm text-muted-foreground">
          Link one Pulse bot number here. Teammates do not scan. Add their numbers on Users,
          then they can mention Pulse in existing groups.
        </p>
        {error ? <ErrorAlert>{error}</ErrorAlert> : null}
        {!status ? (
          <LoadingRows columns={2} />
        ) : (
          <>
            <section className="max-w-2xl space-y-3">
              <h2 className="text-sm font-medium">Connection</h2>
              <p className="text-sm text-muted-foreground">{statusLabel(status)}</p>
              {status.error ? (
                <p className="text-sm text-destructive">{status.error}</p>
              ) : null}
              {status.state === 'qr' && status.qr_data_url ? (
                <img
                  src={status.qr_data_url}
                  alt="WhatsApp link QR code"
                  className="h-56 w-56 rounded-md border bg-white p-2"
                />
              ) : null}
              <div className="flex flex-wrap gap-2">
                {status.state !== 'connected' ? (
                  <Button type="button" size="sm" disabled={busy} onClick={() => void onLink()}>
                    {busy ? 'Starting…' : status.state === 'idle' ? 'Link WhatsApp' : 'Show QR again'}
                  </Button>
                ) : null}
                {status.state !== 'idle' ? (
                  <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => void onUnlink()}>
                    {busy ? 'Disconnecting…' : 'Disconnect'}
                  </Button>
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground">
                On the Pulse bot phone: WhatsApp → Linked devices → Link a device, then scan.
              </p>
            </section>
            <section className="max-w-sm space-y-2">
              <Label htmlFor="whatsapp-default-project">Default project</Label>
              <Select
                value={status.default_project ?? NONE}
                onValueChange={(value) => void onDefaultProject(value)}
                disabled={savingProject}
              >
                <SelectTrigger id="whatsapp-default-project">
                  <SelectValue placeholder="Choose a project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.identifier}>
                      {project.identifier} — {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                New work lands here unless the message names another project.
                Allowlisted users must be members of that project.
              </p>
            </section>
          </>
        )}
      </div>
    </PageFrame>
  );
}
